/*
<summary>
  Displays list of store motivations with view and delete options.
  Developer: Sahil Bharti | Date: 14-April-2025
</summary>
<returns>JSX</returns>
*/
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { Button, Container } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { useStore } from "../../../contexts/store-provider";
import Grid, { getColumns } from "../../../shared-components/Grid/Grid";
import { ColumnType } from "../../../constants/enums/grid-column-type-enum";
import LanguageSlider from "../../../shared-components/language-slider/language-slider";
import useWindowDimensions, {
  getTableMaxHeight,
} from "../../../helpers/window-resize-helper";
import { getStoreIdFromLocalStorage } from "../../../helpers/localstorage-helper";
import { CellContext } from "@tanstack/react-table";
import FormLoader from "../../../shared-components/FormLoader/FormLoader";
import Nodata from "../../../shared-components/no-data-svg/no-data-svg";

interface IProps {
  id: number;
  showDeletePopup: boolean;
  deleteToggleHandler: () => void;
  setSelectedId: React.Dispatch<React.SetStateAction<number>>;
  selectedId: number;
  editHandler: (row: { Id: number }) => void;
}

const MotivationInStoreListView: React.FC<IProps> = (props: IProps) => {
  const { masterManagementStore } = useStore();
  const {
    deleteToggleHandler,
    setSelectedId,
    editHandler,
  } = props;

  const {
    inProgress,
    motivationInStoreState,
    motivationInStoreList,
    addUpdateMotivationInStoreState,
    deleteMotivationInStoreState,
  } = masterManagementStore;

  const [data, setData] = useState<any[]>([]);

  useEffect(() => {
    const transformedData = motivationInStoreList.map((item) => ({
      Id: item.Id ?? item.StoreMotivation.Id,
      MotivationName: item.StoreMotivation.MotivationName,
      Description: item.StoreMotivation.Description,
      StoreId: item.StoreId,
      IsUsed: item.IsUsed,
    }));

    setData(transformedData);
  }, [motivationInStoreList]);

  // const [filterData, setFilterData] = useState<any>({...filter});

  const { height, width } = useWindowDimensions();

  const handleDelete = (id: number) => {
    setSelectedId(id);
    deleteToggleHandler();
  };

  const handleShow = (id: number) => {
    const row = data.find((item) => item.Id === id);
    if (row) {
      editHandler(row);
    }
  };

  const columns = getColumns<any, number>([
    {
      columnType: ColumnType.Hidden,
      accessorKey: "Id",
    },
    {
      columnType: ColumnType.TextHeader,
      accessorKey: "MotivationName",
      displayName: "motivation_name",
    },
    {
      columnType: ColumnType.JsxElement,
      jsxElementFormatter: (info: CellContext<any, any>) => {
        const row = info.row.original;

        return row.IsUsed ? (
          <Button
            variant="danger"
            size="sm"
            onClick={() => handleDelete(row.Id)}
          >
            <i className="bi bi-trash me-2"></i>
            <FormattedMessage id="remove_from_store" />
          </Button>
        ) : (
          <Button className ="btn-primary btn-sm"  onClick={() => handleShow(row.Id)}>
            <i className="bi bi-plus-lg  me-2"></i>
            <FormattedMessage id="add_to_store" />
          </Button>
        );
      },
      accessorKey: "isAction",
      displayName: "",
      headerClassName: "last-column",
    },
  ]);

  return (
    <React.Fragment>
      <FormLoader
        loading={
          inProgress ||
          addUpdateMotivationInStoreState.inProgress ||
          deleteMotivationInStoreState.inProgress
        }
      />
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <div className="col-6">
            <div className="page_title_wrap">
              <h1 className="page_title">
                <FormattedMessage id="store_motivation_settings" />
              </h1>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <FormattedMessage id="various_settings" />
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  <FormattedMessage id="store_motivation_settings" />
                </li>
              </ol>
            </div>
          </div>
          <div className="col-6">
            <ul className="add-list">
              <li>
                <LanguageSlider />
              </li>
            </ul>
          </div>
        </div>
      </div>
      <Container fluid className="p-0 my-3">
        {data.length !== 0 ? (
          <div>
            <div
              className="table-responsive"
              style={getTableMaxHeight(height, width)}
              data-testid="table"
            >
              {data && (
                <Grid
                  data={data}
                  columns={columns}
                  isInProgress={motivationInStoreState.inProgress}
                  hiddenColumns={["Id"]}
                />
              )}
            </div>
          </div>
        ) : (
          <div className="no_data_table">
            <div className="no_data w-100 text-capitalize text-center">
              {/* <img src={""} alt="NOData" className="NOData" /> */}
              <Nodata />
              <h1 className="fs-4 fw-semi-bold">
                <FormattedMessage id="message_no_datafound" />
              </h1>
            </div>
          </div>
        )}
      </Container>
    </React.Fragment>
  );
};

export default observer(MotivationInStoreListView);
